import React, { ReactNode } from 'react';
import { createRoot } from 'react-dom/client';
import { RouterProvider } from 'react-router-dom';
import { loadRecaptchaScript } from './services/recaptcha';
import posthog from 'posthog-js';
import { store } from './store/store';
import { StoreEvent } from './store/store.types';
import { PostHogProvider } from '@posthog/react';
import { AppContextProvider } from './context/AppContext';
import { tracer } from './services/tracer';
import { cookieProInitialiser } from './services/cookiePro';
import { buildRouter, ClientRoutes } from './routes/routes';
import { useCDNLoader } from '@domgen/base-design-system-2.0/hooks/useCDNLoader';
import { toBool } from '@domgen/shared/utils/vite-env/toBool';
import { initPostHog } from './services/posthog/init';
import { log } from '@domgen/shared/utils/log';
import { getSystemCode } from './utils/getSystemCode';

export const createApp = async ({
  clientLogo,
  routes,
}: {
  clientLogo: ReactNode;
  routes?: ClientRoutes;
}) => {
  if (typeof window !== 'undefined') {
    tracer();
  }
  const environment = import.meta.env.MODE as 'sandbox' | 'development' | 'qa' | 'production';

  const { initialise } = cookieProInitialiser(environment);
  initialise(
    import.meta.env.VITE_COOKIE_PRO_BRAND_SCRIPT_ID,
    import.meta.env.VITE_COOKIE_PRO_SRC,
    ['sandbox', 'development', 'qa', 'production'],
    'cookiePro',
  );

  const { loadScript } = useCDNLoader(environment);
  if (toBool(import.meta.env.VITE_ADOBE_TAGS_ENABLED)) {
    loadScript(import.meta.env.VITE_ADOBE_TAG_MANAGER_SCRIPT_URL, 'adobeTagManager', [
      'sandbox',
      'development',
      'qa',
      'production',
    ]);
  }

  const params = new URLSearchParams(window.location.search);
  const mn = params.get('mn') ?? undefined;
  const sn = params.get('sn') ?? undefined;
  const rs = params.get('rs') ?? undefined;
  const urlParams = mn || sn || rs ? { mn, sn, rs } : undefined;

  await Promise.all([
    loadRecaptchaScript(),
    initPostHog({
      brand: import.meta.env.VITE_BRAND_CODE,
      source: getSystemCode(urlParams?.rs),
    }).catch((e) => log.error({ message: 'Failed to initialize PostHog', data: e })),
  ]);

  store.send({ type: StoreEvent.INITIALISE });

  const container = document.getElementById('root');

  const { getRouteFromPath, router } = buildRouter(routes);

  createRoot(container!).render(
    <React.StrictMode>
      <PostHogProvider client={posthog}>
        <AppContextProvider value={{ clientLogo, getRouteFromPath }}>
          <RouterProvider router={router} />
        </AppContextProvider>
      </PostHogProvider>
    </React.StrictMode>,
  );
};
